<template>
  <!-- Talking with Connect: what it is doing, what each side said in this turn, and Stop -->
  <section class="av" :class="'is-' + state" aria-label="Talking with Connect">
    <div class="av-head">
      <span class="av-orb" :style="{ '--lvl': lvl }" aria-hidden="true">
        <svg viewBox="0 0 24 24"><rect x="9" y="3" width="6" height="11" rx="3" /><path d="M5 11a7 7 0 0 0 14 0" /><path d="M12 18v3" /></svg>
      </span>
      <div class="av-titles">
        <b>{{ stateText }}</b>
        <span v-if="state !== 'ended'">Speak in any language; changes still wait for your Confirm on the card.</span>
      </div>
      <!-- five bars that follow the voice heard or spoken; still when nobody talks -->
      <span class="av-bars" v-if="state === 'listening' || state === 'speaking'" aria-hidden="true">
        <i v-for="(k, i) in barShape" :key="i" :style="{ transform: 'scaleY(' + (0.18 + lvl * k).toFixed(2) + ')' }"></i>
      </span>
      <button v-if="state === 'ended'" type="button" class="av-link" @click="begin">Talk again</button>
      <button type="button" class="av-link" @click="stop">{{ state === "ended" ? "Close" : "Stop" }}</button>
    </div>
    <p class="av-line" v-if="caption.user"><b>You</b>{{ caption.user }}</p>
    <p class="av-line" v-if="caption.model"><b>Connect</b>{{ caption.model }}</p>
    <p class="av-note" v-if="note">{{ note }}</p>
  </section>
</template>

<script>
import { LiveVoice } from "@/utils/liveVoice";

/**
 * Talking with Connect (2026-10-21). Opened by the mic in Connect's chat; the conversation itself is LiveVoice
 * (the browser and Gemini Live), the session and every tool call are Connect's API, as the person. What was said
 * lands in the chat as text, with the charts, files and cards the tools made, in the order they happened.
 */
export default {
  name: "AssistantVoice",
  emits: ["close"],
  data() {
    return { state: "connecting", lvl: 0, caption: { user: "", model: "" }, note: "", barShape: [0.55, 0.85, 1, 0.8, 0.5] };
  },
  computed: {
    stateText() {
      return { connecting: "Connecting…", listening: "Listening…", working: "Working on it…", speaking: "Connect is speaking", ended: "Voice is off" }[this.state] || "";
    },
  },
  mounted() { this.begin(); },
  beforeUnmount() { if (this.voice) this.voice.stop(); },
  methods: {
    /** Starts in the same click as the mic (prepare() before the first await): the browser lets sound start only from a gesture. */
    async begin() {
      this.note = ""; this.caption = { user: "", model: "" }; this.state = "connecting";
      const voice = new LiveVoice({
        state: (s) => { this.state = s; if (s !== "listening" && s !== "speaking") this.lvl = 0; },
        level: (v) => { this.lvl = v; },
        caption: (c) => { this.caption = c; },
        tool: (name, args, heard) => this.tool(name, args, heard),
        save: (t) => this.$store.dispatch("assistantVoiceSave", t),
        end: (reason) => { this.note = reason; },
      });
      this.voice = voice;
      voice.prepare();
      const session = await this.$store.dispatch("assistantVoiceSession", this.where());
      if (!session) { await voice.stop(); this.$emit("close"); return; }
      try {
        await voice.start(session);
      } catch (e) {
        await voice.stop();
        this.state = "ended";
        this.note = e && e.name === "NotAllowedError" ? "Allow the microphone for this site to talk with Connect."
          : e && e.name === "NotFoundError" ? "No microphone was found." : (e && e.message) || "Voice could not start.";
      }
    },
    where() { return { page: this.$route.name, store: this.$store.state.stores.current.storeHash }; },
    /** A tool the model called: run by the API as me; a page it opens opens here. */
    async tool(name, args, heard) {
      try {
        const data = await this.$store.dispatch("assistantVoiceTool", { name, args, question: heard, ...this.where() });
        if (!data.status) return { ok: false, error: data.message || "That is not allowed." };
        if (data.route && data.route !== this.$route.name) this.$router.push({ name: data.route }).catch(() => {});
        return data.result;
      } catch (e) {
        return { ok: false, error: "Connect could not run that just now." };
      }
    },
    async stop() {
      if (this.voice) await this.voice.stop();
      this.$emit("close");
    },
  },
};
</script>

<style scoped>
.av { box-sizing: border-box; flex: 0 0 auto; display: flex; flex-direction: column; gap: 10px; margin: var(--s-2) 0; padding: 14px; border: 1px solid var(--brand-orange-light); border-radius: 16px;
  background: linear-gradient(135deg, var(--accent-primary-bg), transparent 70%), var(--bg-primary); box-shadow: 0 12px 28px -18px var(--brand-orange); }
.av.is-ended { border-color: var(--border-primary); box-shadow: none; background: var(--bg-secondary); }
.av-head { display: flex; align-items: center; gap: 12px; }
/* The ring follows the voice heard (listening) or spoken (speaking): motion that means something, nothing else moves. */
.av-orb { --lvl: 0; flex: none; width: 44px; height: 44px; display: inline-flex; align-items: center; justify-content: center; border-radius: 999px; color: var(--btn-primary-text);
  background: linear-gradient(135deg, var(--brand-orange), #fdba74); box-shadow: 0 0 0 calc(4px + var(--lvl) * 10px) var(--accent-primary-bg), 0 10px 24px -10px var(--brand-orange); transition: box-shadow 90ms linear; }
.av.is-speaking .av-orb { background: linear-gradient(135deg, var(--brand-orange-dark), var(--brand-orange)); }
.av.is-connecting .av-orb, .av.is-working .av-orb { box-shadow: 0 0 0 4px var(--accent-primary-bg); }
.av.is-ended .av-orb { background: var(--text-muted); box-shadow: 0 0 0 4px var(--bg-hover); }
.av-orb svg { width: 20px; height: 20px; fill: none; stroke: currentColor; stroke-width: 2; stroke-linecap: round; stroke-linejoin: round; }
.av-titles { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 2px; }
.av-titles b { font-size: var(--t-body); font-weight: 650; color: var(--text-primary); }
.av-titles span { font-size: var(--t-caption); line-height: var(--t-caption-lh); color: var(--text-muted); }
.av-bars { flex: none; display: inline-flex; align-items: center; gap: 3px; height: 26px; }
.av-bars i { display: block; width: 4px; height: 26px; border-radius: 4px; background: linear-gradient(180deg, #fdba74, var(--brand-orange)); transform-origin: 50% 50%; transition: transform 90ms linear; }
.av-link { flex: none; padding: 0; border: 0; background: transparent; font: inherit; font-size: var(--t-caption); font-weight: 650; color: var(--text-link); cursor: pointer; }
.av-link:hover { text-decoration: underline; }
.av-line { margin: 0; padding: 8px 10px; border-radius: 12px; background: var(--bg-secondary); font-size: var(--t-body-sm); line-height: 1.5; color: var(--text-primary); overflow-wrap: anywhere; }
.av-line b { margin-right: 6px; font-weight: 650; color: var(--brand-orange); }
.av-note { margin: 0; font-size: var(--t-caption); line-height: var(--t-caption-lh); color: var(--text-muted); }
@media (prefers-reduced-motion: reduce) { .av-orb, .av-bars i { transition: none; } }
</style>
